import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import usmApi, { USM_BASE, USM_TOKEN_KEY } from "@/lib/usmApi";
import { toast } from "sonner";
import { X, Save, RotateCw, Eye, Bold, Type } from "lucide-react";
import { ELEMENT_KEYS, ALL_KEYS, ELEMENT_LABEL_TR, SAMPLE_VALUES, defaultElementsFor, sampleText } from "./labelHelpers";

/**
 * Visual label designer — drag-to-position, font controls, live preview.
 *
 * Layout:
 *   [ Toolbar: name + dimensions + barcode_type + default + Save/Önizleme ]
 *   [ SVG Canvas (left)         |  Sidebar (right): Element list + Properties ]
 *
 * The canvas renders an SVG with viewBox in millimetres → mouse coords map
 * directly to mm without arithmetic. Dragging updates the selected element's
 * x/y in-place. Properties panel mirrors the same state for fine-tuning.
 *
 * Live PNG preview from the backend is debounced (400ms) so the user gets a
 * pixel-accurate render after they stop dragging — without spamming the API.
 */
export default function LabelDesigner({ mode, initial, onClose, onSaved }) {
  const [profile, setProfile] = useState(() => normalizeInitial(initial));
  const [selected, setSelected] = useState("stock_code");
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewItemId, setPreviewItemId] = useState("");
  const [showRealPreview, setShowRealPreview] = useState(false);
  const previewBlobRef = useRef(null);

  // Fetch a sample inventory id once for the live PNG preview
  useEffect(() => {
    let mounted = true;
    usmApi.get("/inventory?limit=1").then((r) => {
      if (mounted) setPreviewItemId(r.data?.items?.[0]?.id || "");
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Debounced live PNG preview from backend
  useEffect(() => {
    if (!previewItemId || !showRealPreview) return;
    const t = setTimeout(async () => {
      try {
        const token = localStorage.getItem(USM_TOKEN_KEY);
        const r = await fetch(`${USM_BASE}/labels/preview/${previewItemId}`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            name: profile.name,
            width_mm: profile.width_mm,
            height_mm: profile.height_mm,
            margin_mm: profile.margin_mm,
            barcode_type: profile.barcode_type,
            elements: profile.elements,
            show_fields: profile.show_fields || {},
          }),
        });
        if (!r.ok) return;
        const blob = await r.blob();
        if (previewBlobRef.current) URL.revokeObjectURL(previewBlobRef.current);
        const url = URL.createObjectURL(blob);
        previewBlobRef.current = url;
        setPreviewUrl(url);
      } catch (e) { /* ignore */ }
    }, 400);
    return () => clearTimeout(t);
  }, [profile, previewItemId, showRealPreview]);
  useEffect(() => () => { if (previewBlobRef.current) URL.revokeObjectURL(previewBlobRef.current); }, []);

  // ESC closes the designer (matches Inventory editor + LabelPrintDialog UX)
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const setMeta = (k, v) => setProfile((p) => {
    const next = { ...p, [k]: v };
    // When dimensions change, clamp element positions to new bounds
    if (k === "width_mm" || k === "height_mm" || k === "margin_mm") {
      next.elements = clampElementsToBounds(next.elements, next.width_mm, next.height_mm);
    }
    return next;
  });
  const setEl = (key, patch) => setProfile((p) => ({
    ...p,
    elements: { ...p.elements, [key]: { ...p.elements[key], ...patch } },
  }));
  const toggleVisible = (key) => setEl(key, { visible: !profile.elements[key]?.visible });

  const resetPositions = () => {
    if (!window.confirm("Tüm konum ve yazı tipi ayarları varsayılana sıfırlansın mı? Şablon adı ve boyutları korunacak.")) return;
    setProfile((p) => ({ ...p, elements: defaultElementsFor(p.width_mm, p.height_mm, p.margin_mm, p.show_fields || {}) }));
    toast.success("Konumlar sıfırlandı");
  };

  const save = async () => {
    if (!profile.name?.trim()) { toast.error("Şablon adı gerekli"); return; }
    setSaving(true);
    try {
      const payload = {
        name: profile.name.trim(),
        width_mm: Number(profile.width_mm),
        height_mm: Number(profile.height_mm),
        margin_mm: Number(profile.margin_mm),
        barcode_type: profile.barcode_type,
        is_default: !!profile.is_default,
        show_fields: profile.show_fields || {},
        elements: profile.elements,
      };
      if (mode === "create") await usmApi.post("/labels/profiles", payload);
      else                   await usmApi.patch(`/labels/profiles/${initial.id}`, payload);
      toast.success("Şablon kaydedildi");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kayıt başarısız");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div data-testid="tpl-designer" className="bg-white w-full max-w-7xl max-h-[96vh] flex flex-col" onClick={(e) => e.stopPropagation()}>

        {/* ─── Toolbar ───────────────────────────────────────────────── */}
        <div className="px-6 py-3 border-b border-[#E7E5E4] flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 mr-2">
            <Type size={16} className="text-[#C8A96A]" />
            <h2 className="font-heading text-lg">{mode === "create" ? "Yeni Etiket Şablonu" : "Şablon Tasarımcısı"}</h2>
          </div>
          <input data-testid="tpl-name" value={profile.name} onChange={(e) => setMeta("name", e.target.value)}
            placeholder="Şablon adı" className="flex-1 min-w-[180px] max-w-[300px] px-3 py-2 border border-[#E7E5E4] text-sm" />
          <NumInput testid="tpl-width"  label="G mm"   value={profile.width_mm}  onChange={(v) => setMeta("width_mm", v)}  min={10} max={200} />
          <NumInput testid="tpl-height" label="Y mm"   value={profile.height_mm} onChange={(v) => setMeta("height_mm", v)} min={10} max={200} />
          <NumInput testid="tpl-margin" label="Kenar mm" value={profile.margin_mm} onChange={(v) => setMeta("margin_mm", v)} min={0}  max={10} step={0.5} />
          <select data-testid="tpl-bt" value={profile.barcode_type} onChange={(e) => setMeta("barcode_type", e.target.value)}
            className="px-3 py-2 border border-[#E7E5E4] text-sm" title="Barkod tipi">
            <option value="ean8">EAN-8 (önerilen)</option>
            <option value="code128">Code128</option>
            <option value="ean13">EAN-13 (12 hane)</option>
          </select>
          <label className="text-sm flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" data-testid="tpl-is-default" checked={!!profile.is_default} onChange={(e) => setMeta("is_default", e.target.checked)} className="accent-[#C8A96A]" />
            Varsayılan
          </label>
          <div className="flex-1" />
          <button data-testid="tpl-reset" onClick={resetPositions} title="Konumları sıfırla"
            className="text-xs uppercase tracking-[0.2em] px-3 py-2 border border-[#E7E5E4] hover:bg-[#FAFAF9] inline-flex items-center gap-1.5">
            <RotateCw size={13} /> Sıfırla
          </button>
          <button data-testid="tpl-toggle-real-preview" onClick={() => setShowRealPreview((v) => !v)} title="Gerçek baskı önizlemesi"
            className={"text-xs uppercase tracking-[0.2em] px-3 py-2 border inline-flex items-center gap-1.5 " + (showRealPreview ? "border-[#C8A96A] bg-[#FBF5E8]" : "border-[#E7E5E4] hover:bg-[#FAFAF9]")}>
            <Eye size={13} /> {showRealPreview ? "Önizleme Açık" : "Gerçek Önizleme"}
          </button>
          <button data-testid="tpl-save-btn" onClick={save} disabled={saving}
            className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-5 py-2 inline-flex items-center gap-1.5">
            <Save size={13} /> {saving ? "..." : "Kaydet"}
          </button>
          <button onClick={onClose} className="text-[#71717A] hover:bg-[#FAFAF9] p-2"><X size={18} /></button>
        </div>

        {/* Prominent indicator: is this profile the one that "Etiket Yazdır"
            actually uses? A common UX trap was editing template A while the
            print button uses template B (the default). Showing this above
            the canvas makes the relationship obvious. */}
        {mode !== "create" && (
          profile.is_default ? (
            <div data-testid="tpl-active-banner" className="px-6 py-2 bg-emerald-50 border-b border-emerald-200 text-[12px] text-emerald-900 flex items-center gap-2">
              <span className="text-base">⭐</span>
              <span><strong>VARSAYILAN ŞABLON</strong> · Bu şablonda yapılan değişiklikler tüm etiket baskılarına yansır.</span>
            </div>
          ) : (
            <div data-testid="tpl-inactive-banner" className="px-6 py-2 bg-amber-50 border-b border-amber-200 text-[12px] text-amber-900 flex items-center gap-2">
              <span className="text-base">⚠</span>
              <span>
                <strong>Bu şablon varsayılan DEĞİL.</strong> &quot;Etiket Yazdır&quot; butonu varsayılan şablonu kullanır — yaptığınız değişiklikler baskıya yansımaz. Bu şablonu kullanmak için yukarıdaki <em>&quot;Varsayılan&quot;</em> kutusunu işaretleyip kaydedin <strong>veya</strong> envanter üzerinden yazdırırken şablon listesinden bunu seçin.
              </span>
            </div>
          )
        )}
        {/* ─── Body grid: canvas | sidebar ───────────────────────────── */}
        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[1fr_340px]">
          {/* Canvas */}
          <div className="bg-[#FAFAF9] p-6 overflow-auto flex flex-col items-center justify-center">
            <DesignerCanvas
              profile={profile}
              selected={selected}
              onSelect={setSelected}
              onMoveElement={(key, x, y) => setEl(key, { x, y })}
              onResizeBarcode={(w, h) => setEl("barcode", { width_mm: w, height_mm: h })}
            />
            {showRealPreview && previewUrl && (
              <div className="mt-6 w-full max-w-md">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Gerçek Baskı Önizlemesi (Backend)</div>
                <img data-testid="tpl-real-preview" src={previewUrl} alt=""
                  style={{
                    width: `${profile.width_mm * 3.78 * 1.5}px`,
                    height: `${profile.height_mm * 3.78 * 1.5}px`,
                    objectFit: "contain",
                    background: "white",
                    imageRendering: "pixelated",
                  }}
                  className="border border-[#E7E5E4] shadow-sm" />
                <PrintMeasurementOverlay profile={profile} previewUrl={previewUrl} />
              </div>
            )}
            <div className="mt-4 text-[10px] uppercase tracking-[0.2em] text-[#A8A29E]">
              {profile.width_mm} × {profile.height_mm} mm  ·  {profile.barcode_type}  ·  Argox / BarTender Uyumlu
            </div>
          </div>

          {/* Sidebar */}
          <div className="border-l border-[#E7E5E4] overflow-y-auto bg-white">
            <ElementList
              elements={profile.elements}
              selected={selected}
              onSelect={setSelected}
              onToggleVisible={toggleVisible}
            />
            <PropertiesPanel
              selectedKey={selected}
              element={profile.elements[selected]}
              profile={profile}
              onChange={(patch) => setEl(selected, patch)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────  SVG canvas  ─────────────────────────────

function DesignerCanvas({ profile, selected, onSelect, onMoveElement, onResizeBarcode }) {
  const W = profile.width_mm;
  const H = profile.height_mm;
  // Use a zoom that fits the average screen — small labels get more pixels
  const PX_PER_MM = W <= 50 ? 8 : (W <= 80 ? 6 : 5);
  const svgRef = useRef(null);
  const [drag, setDrag] = useState(null); // { key, offsetX, offsetY, kind }

  const mmFromEvent = useCallback((evt) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const sp = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: sp.x, y: sp.y };
  }, []);

  const onPointerDown = (key, kind) => (e) => {
    e.stopPropagation();
    onSelect(key);
    const { x, y } = mmFromEvent(e);
    const el = profile.elements[key];
    setDrag({ key, kind, offsetX: x - el.x, offsetY: y - el.y, startW: el.width_mm, startH: el.height_mm, startX: x, startY: y });
    try { e.target.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
  };

  const onPointerMove = (e) => {
    if (!drag) return;
    const { x, y } = mmFromEvent(e);
    if (drag.kind === "move") {
      const nx = clamp(round2(x - drag.offsetX), 0, W);
      const ny = clamp(round2(y - drag.offsetY), 0, H);
      onMoveElement(drag.key, nx, ny);
    } else if (drag.kind === "resize-barcode") {
      const nw = clamp(round2(drag.startW + (x - drag.startX)), 5, W);
      const nh = clamp(round2(drag.startH + (y - drag.startY)), 3, H);
      onResizeBarcode(nw, nh);
    }
  };

  const onPointerUp = () => setDrag(null);

  const visibleKeys = ALL_KEYS.filter((k) => profile.elements[k]?.visible);
  const margin = profile.margin_mm || 0;

  return (
    <svg
      ref={svgRef}
      data-testid="tpl-canvas"
      width={W * PX_PER_MM}
      height={H * PX_PER_MM}
      viewBox={`0 0 ${W} ${H}`}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
      style={{ background: "white", boxShadow: "0 4px 24px rgba(0,0,0,.10)", touchAction: "none", maxWidth: "100%" }}
    >
      {/* Margin guides */}
      <rect x={margin} y={margin} width={Math.max(0, W - 2 * margin)} height={Math.max(0, H - 2 * margin)}
        fill="none" stroke="#E7E5E4" strokeWidth={0.1} strokeDasharray="0.5,0.5" />

      {/* Barcode (rendered first so text sits on top of it visually) */}
      {profile.elements.barcode?.visible && (
        <BarcodeNode el={profile.elements.barcode} selected={selected === "barcode"}
          onPointerDown={onPointerDown("barcode", "move")}
          onResizePointerDown={onPointerDown("barcode", "resize-barcode")} />
      )}

      {/* Text elements */}
      {ELEMENT_KEYS.filter((k) => profile.elements[k]?.visible).map((key) => (
        <TextNode key={key} elKey={key} el={profile.elements[key]} selected={selected === key}
          onPointerDown={onPointerDown(key, "move")} />
      ))}

      {!visibleKeys.length && (
        <text x={W/2} y={H/2} textAnchor="middle" fontSize={2} fill="#A8A29E">Hiç görünür alan yok — sağdaki listeden açın</text>
      )}
    </svg>
  );
}

function BarcodeNode({ el, selected, onPointerDown, onResizePointerDown }) {
  // Render fake bars as a placeholder so position is obvious without
  // calling the backend on every move.
  const bars = [];
  const start = el.x;
  const end = el.x + el.width_mm;
  let cx = start;
  while (cx < end - 0.4) {
    const bw = 0.2 + Math.random() * 0.35; // hashed but visually plausible
    bars.push(<rect key={cx} x={cx} y={el.y} width={bw} height={el.height_mm * 0.78} fill="black" />);
    cx += bw + (0.18 + Math.random() * 0.3);
  }
  return (
    <g style={{ cursor: "move" }} onPointerDown={onPointerDown}>
      {/* Background frame */}
      <rect x={el.x} y={el.y} width={el.width_mm} height={el.height_mm} fill="white"
        stroke={selected ? "#C8A96A" : "transparent"} strokeWidth={0.25} />
      {bars}
      <text x={el.x + el.width_mm / 2} y={el.y + el.height_mm - 0.4} fontSize={1.6} textAnchor="middle"
        fontFamily="DejaVu Sans, Arial, sans-serif" fill="black">{SAMPLE_VALUES.stock_code}</text>
      {selected && (
        <>
          <rect x={el.x - 0.3} y={el.y - 0.3} width={el.width_mm + 0.6} height={el.height_mm + 0.6} fill="none"
            stroke="#C8A96A" strokeWidth={0.2} strokeDasharray="0.5,0.4" />
          {/* SE resize handle */}
          <rect data-testid="tpl-bc-resize" x={el.x + el.width_mm - 1.2} y={el.y + el.height_mm - 1.2} width={1.6} height={1.6}
            fill="#C8A96A" style={{ cursor: "nwse-resize" }} onPointerDown={onResizePointerDown} />
        </>
      )}
    </g>
  );
}

function TextNode({ elKey, el, selected, onPointerDown }) {
  const text = sampleText(elKey);
  const display = el.uppercase ? (text || "").toUpperCase() : text;
  const anchor = el.align === "right" ? "end" : el.align === "center" ? "middle" : "start";
  // Approx baseline correction so the y coord refers to the top-left of the text (matches Pillow anchor="la")
  const fontMm = (Number(el.font_size) || 6) * 0.35278; // pt → mm (1pt = 0.35278mm)
  const baseY = el.y + fontMm * 0.86;
  return (
    <g style={{ cursor: "move" }} onPointerDown={onPointerDown} data-testid={`tpl-canvas-${elKey}`}>
      <text x={el.x} y={baseY} fontSize={fontMm}
        fontWeight={el.bold ? 700 : 400}
        textAnchor={anchor}
        fontFamily="DejaVu Sans, Arial, sans-serif"
        fill="black">{display || `[${ELEMENT_LABEL_TR[elKey]}]`}</text>
      {selected && (
        <SelectionBox x={el.x} y={el.y} width={Math.max(fontMm * 0.6 * (display?.length || 4), 4)} height={fontMm * 1.1} anchor={anchor} />
      )}
    </g>
  );
}

function SelectionBox({ x, y, width, height, anchor }) {
  const left = anchor === "end" ? x - width : anchor === "middle" ? x - width / 2 : x;
  return (
    <rect x={left - 0.3} y={y - 0.3} width={width + 0.6} height={height + 0.6} fill="none"
      stroke="#C8A96A" strokeWidth={0.2} strokeDasharray="0.5,0.4" pointerEvents="none" />
  );
}

// ─────────────────────────────  Sidebar  ─────────────────────────────

function ElementList({ elements, selected, onSelect, onToggleVisible }) {
  return (
    <div className="p-4 border-b border-[#E7E5E4]">
      <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Alanlar</h3>
      <ul className="space-y-1">
        {ALL_KEYS.map((key) => {
          const el = elements[key];
          if (!el) return null;
          const isSelected = selected === key;
          return (
            <li key={key} className={"flex items-center gap-2 px-2 py-1.5 cursor-pointer text-sm " + (isSelected ? "bg-[#FBF5E8] border-l-2 border-[#C8A96A]" : "hover:bg-[#FAFAF9] border-l-2 border-transparent")}
              onClick={() => onSelect(key)} data-testid={`tpl-list-${key}`}>
              <input type="checkbox" checked={!!el.visible} onChange={(e) => { e.stopPropagation(); onToggleVisible(key); }}
                className="accent-[#C8A96A]" data-testid={`tpl-show-${key}`} onClick={(e) => e.stopPropagation()} />
              <span className={el.visible ? "" : "text-[#A8A29E] line-through"}>{ELEMENT_LABEL_TR[key]}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ────────── Barcode geometry helpers (mirror backend `labels.py`) ──────────
// Exposes the same module-width math used by the server so we can give the
// designer live feedback on actual bar span and scanner reliability.
const DPI = 300;
const PX_PER_MM = DPI / 25.4;
const MM_PER_PX = 25.4 / DPI;
const MIN_MODULE_PX = 2;   // Phase 41 — matches backend `_draw_label` floor
const MAX_MODULE_PX = 14;
const QUIET_MODULES_EACH = 10;

function expectedModules(bcType, value) {
  // Match the backend's `_expected_modules` heuristic.
  const v = String(value || "");
  if (bcType === "ean8") return 67;
  if (bcType === "ean13") return 95;
  // Code128 — start(11) + each char(11) + checksum(11) + stop(13) ≈ 35 + 11*(n+1)
  return 35 + 11 * (v.length + 1);
}

function computeBarcodeGeometry(bcType, value, requestedWidthMm) {
  const symbolModules = expectedModules(bcType, value);
  const totalModules = symbolModules + 2 * QUIET_MODULES_EACH;
  const blockWPx = Math.max(1, Math.round(requestedWidthMm * PX_PER_MM));
  const maxModulePx = Math.floor(blockWPx / totalModules);
  // Clamped to printer-safe bounds.
  const modulePx = Math.max(MIN_MODULE_PX, Math.min(MAX_MODULE_PX, maxModulePx));
  const moduleMm = modulePx * MM_PER_PX;
  const naturalWidthMm = totalModules * moduleMm;
  // Most thermal printers (Argox / TSC / Zebra) print at 203 DPI → 0.125 mm/dot.
  const dots203 = moduleMm * 203 / 25.4;
  // Was the requested width honoured?  When the symbol can't fit at 3 px the
  // backend still renders at 3 px (no scaling) → bar span may exceed the
  // request and overflow into adjacent text. We warn the designer.
  const fits = naturalWidthMm <= requestedWidthMm + 0.1;
  // Scanner-reliability grade.
  const tier =
    dots203 >= 3.5 ? { label: "MÜKEMMEL", color: "#15803D" } :
    dots203 >= 2.5 ? { label: "GÜVENLİ",  color: "#15803D" } :
    dots203 >= 2.0 ? { label: "SINIRDA",  color: "#B45309" } :
                     { label: "RİSKLİ",   color: "#B91C1C" };
  return { modulePx, moduleMm, naturalWidthMm, dots203, fits, tier, totalModules };
}

function PropertiesPanel({ selectedKey, element, profile, onChange }) {
  // Listen for the "snap to physical minimum" event dispatched by the live
  // geometry readout — instantly updates the width_mm input to whatever the
  // backend reports as the achievable floor.
  useEffect(() => {
    if (selectedKey !== "barcode") return;
    const handler = (e) => {
      const minMm = e.detail;
      if (typeof minMm === "number" && minMm > 0) {
        onChange({ width_mm: Math.max(5, Math.min(profile.width_mm, minMm)) });
      }
    };
    window.addEventListener("usm-bc-snap-min", handler);
    return () => window.removeEventListener("usm-bc-snap-min", handler);
  }, [selectedKey, onChange, profile.width_mm]);
  if (!element) return null;
  const isBarcode = selectedKey === "barcode";
  const isRingSize = selectedKey === "ring_size";

  return (
    <div className="p-4">
      <h3 className="text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-3">Özellikler — {ELEMENT_LABEL_TR[selectedKey]}</h3>
      {isRingSize && (
        <p className="text-[11px] text-[#71717A] bg-[#FBF5E8] border border-[#F5F5F4] px-2.5 py-2 mb-3 leading-relaxed">
          Yüzük Ölçüsü yalnızca kategori &ldquo;Yüzük&rdquo; olduğunda yazdırılır. Diğer kategorilerde otomatik gizlenir.
        </p>
      )}

      <div className="grid grid-cols-2 gap-3">
        <PropField testid={`tpl-prop-x-${selectedKey}`} label="X (mm)" value={element.x}
          onChange={(v) => onChange({ x: clamp(v, 0, profile.width_mm) })} min={0} max={profile.width_mm} step={0.1} />
        <PropField testid={`tpl-prop-y-${selectedKey}`} label="Y (mm)" value={element.y}
          onChange={(v) => onChange({ y: clamp(v, 0, profile.height_mm) })} min={0} max={profile.height_mm} step={0.1} />

        {isBarcode ? (
          <>
            <PropField testid="tpl-prop-w" label="Genişlik (mm)" value={element.width_mm}
              onChange={(v) => onChange({ width_mm: clamp(v, 5, profile.width_mm) })} min={5} max={profile.width_mm} step={0.5} />
            <PropField testid="tpl-prop-h" label="Yükseklik (mm)" value={element.height_mm}
              onChange={(v) => onChange({ height_mm: clamp(v, 3, profile.height_mm) })} min={3} max={profile.height_mm} step={0.5} />

            <div className="col-span-2 mt-1">
              <label className={lblCls}>Hızlı Genişlik</label>
              <div className="grid grid-cols-5 gap-1">
                {[
                  { l: "20",   v: 20 },
                  { l: "25",   v: 25 },
                  { l: "30",   v: 30 },
                  { l: "50",   v: 50 },
                  { l: "Tam",  v: profile.width_mm },
                ].map((p) => {
                  const isActive = Math.abs((element.width_mm || 0) - p.v) < 0.5;
                  return (
                    <button
                      key={p.l}
                      data-testid={`tpl-prop-bc-preset-${p.l}`}
                      onClick={() => onChange({ width_mm: clamp(p.v, 5, profile.width_mm) })}
                      className={"py-1.5 text-[11px] uppercase tracking-wider border " + (isActive ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E7E5E4] hover:bg-[#FAFAF9]")}
                    >
                      {p.l}{p.l !== "Tam" && "mm"}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Live-calculated barcode geometry feedback. Mirrors backend math
                so the designer always sees what will actually be printed. */}
            <BarcodeGeometryReadout
              profile={profile}
              element={element}
            />
          </>
        ) : (
          <>
            <PropField testid={`tpl-prop-fs-${selectedKey}`} label="Yazı Boyutu (pt)" value={element.font_size}
              onChange={(v) => onChange({ font_size: clamp(v, 3, 40) })} min={3} max={40} step={0.5} />
            <div>
              <label className={lblCls}>Hizalama</label>
              <div className="grid grid-cols-3 gap-1">
                {["left", "center", "right"].map((a) => (
                  <button key={a} data-testid={`tpl-prop-align-${selectedKey}-${a}`} onClick={() => onChange({ align: a })}
                    className={"py-1.5 text-[11px] uppercase tracking-wider border " + (element.align === a ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E7E5E4] hover:bg-[#FAFAF9]")}>
                    {a === "left" ? "Sol" : a === "center" ? "Orta" : "Sağ"}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 col-span-2 text-sm cursor-pointer mt-1">
              <input type="checkbox" data-testid={`tpl-prop-bold-${selectedKey}`} checked={!!element.bold}
                onChange={(e) => onChange({ bold: e.target.checked })} className="accent-[#C8A96A]" />
              <Bold size={13} /> Kalın
            </label>
            <label className="flex items-center gap-2 col-span-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid={`tpl-prop-uc-${selectedKey}`} checked={!!element.uppercase}
                onChange={(e) => onChange({ uppercase: e.target.checked })} className="accent-[#C8A96A]" />
              BÜYÜK HARF
            </label>
          </>
        )}
      </div>
    </div>
  );
}

function BarcodeGeometryReadout({ profile, element }) {
  // Use a placeholder value when no real item is being previewed — Code128
  // is value-length sensitive (EAN-8 is not), so we estimate against a
  // typical 9-char UZG stock code which matches the live system.
  const sample = profile.barcode_type === "ean8" ? "12345670"
              : profile.barcode_type === "ean13" ? "123456789012"
              : "UZG000049";
  const requestedWidth = element.width_mm || profile.width_mm;
  // Local (instant) estimate as the default — replaced when the server
  // confirms the EXACT geometry for Code128 (whose encoded length depends
  // on value content). Avoids a flicker between user input and API call.
  const localGeom = computeBarcodeGeometry(profile.barcode_type, sample, requestedWidth);
  const [serverGeom, setServerGeom] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const tid = setTimeout(async () => {
      try {
        const r = await usmApi.get("/labels/barcode-geometry", {
          params: { bc_type: profile.barcode_type, value: sample, width_mm: requestedWidth },
        });
        if (!cancelled) setServerGeom(r.data);
      } catch {
        if (!cancelled) setServerGeom(null);
      }
    }, 250); // debounce while user drags the width input
    return () => { cancelled = true; clearTimeout(tid); };
  }, [profile.barcode_type, sample, requestedWidth]);

  const g = serverGeom ? {
    moduleMm: serverGeom.module_mm,
    modulePx: serverGeom.module_px,
    naturalWidthMm: serverGeom.natural_w_mm,
    dots203: serverGeom.dots_203_dpi,
    fits: serverGeom.fits,
    minAchievableMm: serverGeom.min_achievable_w_mm,
    requestBelowMin: requestedWidth < (serverGeom.min_achievable_w_mm || 0),
    tier: {
      label: serverGeom.tier,
      color: serverGeom.tier === "MÜKEMMEL" || serverGeom.tier === "GÜVENLİ" ? "#15803D"
           : serverGeom.tier === "SINIRDA" ? "#B45309" : "#B91C1C",
    },
  } : { ...localGeom, minAchievableMm: null, requestBelowMin: false };

  return (
    <div className="col-span-2 mt-3 border border-[#E7E5E4] bg-[#FAFAF9] p-3 text-[11px] space-y-1.5">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1.5 flex items-center justify-between">
        <span>Canlı Hesap</span>
        {!serverGeom && <span className="text-[9px] normal-case tracking-normal text-[#A8A29E]">(tahmini)</span>}
      </div>
      <Readout label="Toplam Genişlik" value={`${g.naturalWidthMm.toFixed(1)} mm (barlar + sessiz alan)`} testid="tpl-bc-actual-w" />
      <Readout label="Modül (X-dimension)" value={`${g.moduleMm.toFixed(3)} mm (${g.modulePx} px @ 300 DPI)`} testid="tpl-bc-module-mm" />
      <Readout label="Termal Yazıcı (203 DPI)" value={`${g.dots203.toFixed(1)} dot`} testid="tpl-bc-dots-203" />
      {g.minAchievableMm != null && (
        <Readout label="Fiziksel Minimum" value={`${g.minAchievableMm.toFixed(1)} mm`} testid="tpl-bc-min-w" />
      )}
      <div className="flex items-center justify-between pt-1">
        <span className="text-[#71717A]">Tarama Güvenilirliği</span>
        <span
          data-testid="tpl-bc-tier"
          className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 border"
          style={{ color: g.tier.color, borderColor: g.tier.color }}
        >
          ● {g.tier.label}
        </span>
      </div>
      {g.requestBelowMin && (
        <div data-testid="tpl-bc-below-min" className="mt-2 text-[10.5px] text-red-700 bg-red-50 border border-red-200 px-2 py-1.5 leading-tight">
          <strong>İstediğiniz genişlik fiziksel olarak mümkün değil.</strong> Bu barkod en az {g.minAchievableMm.toFixed(1)} mm yer kaplar — daha küçük modüller (1 piksel) termal yazıcıda basılamaz. <em>Genişlik {requestedWidth} mm girseniz bile çıktı {g.minAchievableMm.toFixed(1)} mm olacak.</em>
        </div>
      )}
      {g.minAchievableMm != null && (
        <div className="mt-2">
          <button
            type="button"
            data-testid="tpl-bc-snap-min"
            onClick={() => {
              // Snap the width input to the achievable minimum — instant fix
              // for users hitting the "why won't it shrink further?" wall.
              const ev = new CustomEvent("usm-bc-snap-min", { detail: g.minAchievableMm });
              window.dispatchEvent(ev);
            }}
            className="w-full text-[10px] uppercase tracking-[0.15em] py-1.5 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
          >
            Genişliği fiziksel minimuma getir ({g.minAchievableMm.toFixed(1)} mm)
          </button>
        </div>
      )}
      {!g.fits && !g.requestBelowMin && (
        <div data-testid="tpl-bc-overflow" className="mt-2 text-[10.5px] text-red-700 bg-red-50 border border-red-200 px-2 py-1.5 leading-tight">
          ⚠ İstenen genişlik bu kod için çok dar. Minimum modül (0.169 mm) ile bile barkod {g.naturalWidthMm.toFixed(1)} mm yer kaplayacak ve elemanın kutusunu aşacak. Daha kısa bir kod (örn. EAN-8) veya daha geniş bir kutu seçin.
        </div>
      )}
      {g.fits && g.dots203 < 2.5 && (
        <div className="mt-2 text-[10.5px] text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1.5 leading-tight">
          ℹ Bu modül genişliği termal yazıcı için sınırda. Mümkünse barkod genişliğini biraz artırın veya tarayıcınızı daha yakın tutun.
        </div>
      )}
      {profile.barcode_type !== "ean8" && requestedWidth < 35 && (
        <div className="mt-2 text-[10.5px] text-sky-800 bg-sky-50 border border-sky-200 px-2 py-1.5 leading-tight">
          💡 Dar etiketler için ipucu: bu boyutta <strong>EAN-8</strong> tipini seçerseniz barkod yaklaşık 22 mm&apos;ye sığar — Code128 daha fazla yer kaplar.
        </div>
      )}
    </div>
  );
}

function Readout({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#71717A]">{label}</span>
      <span data-testid={testid} className="font-mono text-[#0A0A0A]">{value}</span>
    </div>
  );
}

function PropField({ testid, label, value, onChange, min, max, step = 0.1 }) {
  return (
    <div>
      <label className={lblCls}>{label}</label>
      <input data-testid={testid} type="number" min={min} max={max} step={step} value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))} className={inputCls} />
    </div>
  );
}

function NumInput({ testid, label, value, onChange, min, max, step = 0.5 }) {
  return (
    <label className="text-[11px] uppercase tracking-wider text-[#71717A] flex items-center gap-1.5">
      {label}
      <input data-testid={testid} type="number" min={min} max={max} step={step} value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="w-16 px-2 py-1.5 border border-[#E7E5E4] text-sm" />
    </label>
  );
}

// ─────────────────────────────  Utils  ─────────────────────────────

function normalizeInitial(initial) {
  const p = { ...initial };
  p.elements = p.elements || defaultElementsFor(p.width_mm, p.height_mm, p.margin_mm, p.show_fields);
  return p;
}

function clampElementsToBounds(elements, W, H) {
  const out = {};
  for (const k of Object.keys(elements)) {
    const e = { ...elements[k] };
    if (typeof e.x === "number") e.x = clamp(e.x, 0, W);
    if (typeof e.y === "number") e.y = clamp(e.y, 0, H);
    if (k === "barcode") {
      if (typeof e.width_mm === "number")  e.width_mm  = clamp(e.width_mm, 5, W);
      if (typeof e.height_mm === "number") e.height_mm = clamp(e.height_mm, 3, H);
    }
    out[k] = e;
  }
  return out;
}

function clamp(n, lo, hi) { return Math.min(Math.max(n, lo), hi); }
function round2(n) { return Math.round(n * 100) / 100; }

const lblCls = "text-[10px] uppercase tracking-[0.15em] text-[#71717A] block mb-1";
const inputCls = "w-full px-2.5 py-1.5 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-sm";


/**
 * Print preview measurement overlay.
 *
 * Renders an HTML5 canvas analysis of the live backend-rendered label PNG
 * to show the cashier:
 *   • Total label width
 *   • Actual barcode-bar span (mm)
 *   • Left / right quiet zone (mm)
 *   • Utilisation %
 *
 * Pure client-side image analysis — no server roundtrip beyond the existing
 * preview URL.
 */
function PrintMeasurementOverlay({ profile, previewUrl }) {
  const [m, setM] = React.useState(null);

  React.useEffect(() => {
    if (!previewUrl) { setM(null); return; }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.width; c.height = img.height;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const data = ctx.getImageData(0, 0, img.width, img.height).data;
        // For each col, check if any row above 60% of height has a dark pixel.
        const W = img.width, H = img.height;
        const topRows = Math.floor(H * 0.7);
        const colHasBar = new Array(W).fill(false);
        for (let y = 0; y < topRows; y++) {
          for (let x = 0; x < W; x++) {
            const i = (y * W + x) * 4;
            const lum = (data[i] + data[i+1] + data[i+2]) / 3;
            if (lum < 128) { colHasBar[x] = true; }
          }
        }
        // Find first/last bar-bearing column
        let left = -1, right = -1;
        for (let x = 0; x < W; x++) if (colHasBar[x]) { left = x; break; }
        for (let x = W - 1; x >= 0; x--) if (colHasBar[x]) { right = x; break; }
        if (left < 0) { setM(null); return; }
        const pxmm = W / Number(profile.width_mm);
        setM({
          label_w_mm: Number(profile.width_mm),
          bar_w_mm: (right - left + 1) / pxmm,
          left_mm: left / pxmm,
          right_mm: (W - right - 1) / pxmm,
          util_pct: ((right - left + 1) / W) * 100,
        });
      } catch { setM(null); }
    };
    img.src = previewUrl;
  }, [previewUrl, profile.width_mm]);

  if (!m) return null;
  const ok85 = m.util_pct >= 85;
  const symmetric = Math.abs(m.left_mm - m.right_mm) < 0.6;

  return (
    <div data-testid="print-measurement-overlay" className="mt-3 grid grid-cols-2 gap-2 text-[10.5px] font-mono">
      <Stat label="Etiket Genişliği" value={`${m.label_w_mm.toFixed(1)} mm`} />
      <Stat
        label="Barkod Genişliği"
        value={`${m.bar_w_mm.toFixed(1)} mm`}
        accent={ok85 ? "ok" : "warn"}
        testid="overlay-bar-width"
      />
      <Stat label="Sol Boşluk" value={`${m.left_mm.toFixed(2)} mm`} testid="overlay-left-margin" />
      <Stat label="Sağ Boşluk" value={`${m.right_mm.toFixed(2)} mm`} testid="overlay-right-margin" />
      <Stat
        label="Kullanım"
        value={`${m.util_pct.toFixed(1)} %`}
        accent={ok85 ? "ok" : "warn"}
        testid="overlay-utilisation"
      />
      <Stat
        label="Merkezleme"
        value={symmetric ? "✓ Dengeli" : "Asimetrik"}
        accent={symmetric ? "ok" : "warn"}
        testid="overlay-symmetry"
      />
    </div>
  );
}

function Stat({ label, value, accent, testid }) {
  const accentCls = accent === "ok" ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
    : accent === "warn" ? "bg-amber-50 text-amber-800 ring-amber-200"
    : "bg-[#FAFAF9] text-[#27272A] ring-[#E7E5E4]";
  return (
    <div className={`px-2.5 py-1.5 ring-1 ${accentCls}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.15em] opacity-70">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  );
}
